import { createHostedPaymentPage, createHostedPaymentPageForUpgrade } from './paymentService';
import { getMyCompany, getMySubscription } from './companyService';
import { getLoggedInUserData, getZohoSubscriptionId, setMySubscriptionData } from '../helpers/storageUtils';
import { STORAGE_KEYS } from '../helpers/constants';
import type { MySubscriptionStoredData } from '../helpers/storageUtils';

export interface PlanUpgradeRequest {
  planId: string;
  planName: string;
  planCode: string;
  customerId?: string;
  customerEmail?: string;
  customerCompanyName?: string;
  isPlanChange?: boolean;
}

export interface PlanUpgradeResponse {
  success: boolean;
  hostedPageUrl?: string;
  message: string;
  error?: string;
}

/**
 * Common service for handling plan upgrades from both onboarding and admin hub
 * @param request - Plan upgrade request data
 * @returns Promise with upgrade response
 */
export const upgradePlan = async (request: PlanUpgradeRequest): Promise<PlanUpgradeResponse> => {
  try {
    // Use REFRESH_TOKEN as primary token (consistent with other services)
    const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
    if (!token) {
      throw new Error('No auth token found');
    }

    // Resolve company name, customer ID, and email from request or stored user data (onboarding may not have contactEmail set)
    const storedUserData = localStorage.getItem(STORAGE_KEYS.LOGGED_IN_USER_DATA);
    const loggedInUser = getLoggedInUserData();
    let companyName = request.customerCompanyName || '';
    let customerId = request.customerId || '';
    let customerEmail = request.customerEmail || '';
    if (storedUserData) {
      try {
        const userData = JSON.parse(storedUserData);
        // Get company name from stored user data if not provided
        companyName = userData.company_name || userData.companyName || companyName;
        // Get customer ID (company_id) from stored user data if not provided
        customerId = customerId || userData.company_id || '';
        // Fall back to stored email when empty (e.g. onboarding flow where onboardingData.contactEmail may be missing)
        if (!customerEmail) {
          customerEmail = userData.email_id || userData.email || '';
        }
      } catch (error) {
        console.error('Error parsing stored user data:', error);
      }
    }
    // Final fallback to getLoggedInUserData in case we didn't parse above
    if (!customerEmail && loggedInUser?.email_id) {
      customerEmail = loggedInUser.email_id;
    }

    // When upgrading from /supportSpace, company name may not be in localStorage (Layout only fetches it on /adminHub).
    // Fetch from API so displayName and companyName are never empty for ZOHO.CREATE_HOSTED_PAGE.
    if (!companyName) {
      try {
        const companyResponse = await getMyCompany();
        const fetchedName = companyResponse.name || (companyResponse as { data?: { name?: string } }).data?.name || '';
        if (fetchedName) {
          companyName = fetchedName;
          // Persist to localStorage so future upgrades and Layout don't need to refetch
          const currentStored = localStorage.getItem(STORAGE_KEYS.LOGGED_IN_USER_DATA);
          if (currentStored) {
            try {
              const userData = JSON.parse(currentStored);
              const updated = { ...userData, company_name: fetchedName, companyName: fetchedName };
              localStorage.setItem(STORAGE_KEYS.LOGGED_IN_USER_DATA, JSON.stringify(updated));
            } catch {
              // ignore parse errors when updating storage
            }
          }
        }
      } catch (apiError) {
        console.error('Plan upgrade: could not fetch company name for payload', apiError);
        // continue with empty companyName rather than failing the whole upgrade
      }
    }

    // When upgrading to paid (isPlanChange): always use hosted page update API; fetch subscriptionId if not in storage
    let subscriptionId = getZohoSubscriptionId();
    if (request.isPlanChange) {
      // If subscriptionId not in storage, fetch MY_SUBSCRIPTION and store so we have it for update API
      if (!subscriptionId) {
        try {
          const subscriptionResponse = await getMySubscription(token);
          // API response.data may include plan + zohoSubscriptionId/subscription_id (typed as Plan but can have extra fields)
          const data = subscriptionResponse?.data as unknown as Record<string, unknown> | undefined;
          if (data) {
            setMySubscriptionData(data as MySubscriptionStoredData);
            const id = (data.zohoSubscriptionId ?? data.zoho_subscription_id ?? data.subscription_id) as string | undefined;
            subscriptionId = id && typeof id === 'string' ? id : '';
          }
        } catch (fetchErr) {
          console.error('Plan upgrade: could not fetch subscription for hosted page update', fetchErr);
        }
      }
      if (subscriptionId) {
        // Upgrade flow: call hostedPage/update with subscriptionId and plan { name, planCode }
        const updateData = {
          subscriptionId,
          plan: {
            name: request.planName,
            planCode: request.planCode
          },
          redirectUrl: `${window.location.origin}/payment-success`,
          platform_name: 'prism7'
        };

        const pendingData = {
          planId: request.planId,
          customerId: customerId,
          planName: request.planName,
          planCode: request.planCode,
          isPlanChange: true
        };
        localStorage.setItem('pendingPlanChange', JSON.stringify(pendingData));

        const response = await createHostedPaymentPageForUpgrade(updateData, token);

        // Treat as success when API returned 200 and we have a URL (update API may use different response shape)
        const hostedPageUrl = response.data?.url;
        if (response.success || hostedPageUrl) {
          return {
            success: true,
            hostedPageUrl: hostedPageUrl ?? undefined,
            message: 'Payment page created successfully'
          };
        }
        throw new Error(response.message || 'Failed to create payment page for upgrade');
      }
      // subscriptionId still missing after fetch: backend may not return it for this plan
      throw new Error('Subscription ID is required to upgrade. Please try again or contact support.');
    }

    // New subscription flow (not a plan change): use create hosted page API (no subscriptionId)
    const paymentData = {
      plan: {
        name: request.planName,
        planCode: request.planCode
      },
      redirectUrl: `${window.location.origin}/payment-success`,
      customer: {
        displayName: companyName,
        email: customerEmail,
        companyName: companyName
      }
    };

    if (request.isPlanChange) {
      const pendingData = {
        planId: request.planId,
        customerId: customerId,
        planName: request.planName,
        planCode: request.planCode,
        isPlanChange: true
      };
      localStorage.setItem('pendingPlanChange', JSON.stringify(pendingData));
    }

    const response = await createHostedPaymentPage(paymentData, token);

    if (response.success) {
      return {
        success: true,
        hostedPageUrl: response.data?.url,
        message: 'Payment page created successfully'
      };
    }
    throw new Error(response.message || 'Failed to create payment page');
  } catch (error: any) {
    console.error('Plan upgrade error:', error);
    return {
      success: false,
      message: 'Failed to upgrade plan',
      error: error.message || 'Unknown error occurred'
    };
  }
};

/**
 * Check if there's a pending plan change
 * @returns Pending plan change data if exists
 */
export const getPendingPlanChange = () => {
  try {
    const pendingData = localStorage.getItem('pendingPlanChange');
    return pendingData ? JSON.parse(pendingData) : null;
  } catch (error) {
    console.error('Error parsing pending plan change:', error);
    return null;
  }
};

/**
 * Clear pending plan change data
 */
export const clearPendingPlanChange = () => {
  localStorage.removeItem('pendingPlanChange');
};
